import { useState, useRef } from 'react';
import { ScrollReveal, StaggerContainer, staggerItem } from '@/components/Animations';
import { motion } from 'framer-motion';
import { MapPin, Send, Facebook, Paperclip, X } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const HERO_IMG = 'https://static.wixstatic.com/media/90a441_38f29926d8bc48beb2c772a854993c59~mv2.jpg/v1/fill/w_1905,h_800,al_t,q_85/90a441_38f29926d8bc48beb2c772a854993c59~mv2.jpg';
const POSTER_IMG = 'https://static.wixstatic.com/media/bf3085_3e0aa56a5a654f80b6b63b6cb4978b09~mv2.png/v1/fill/w_500,h_700,al_c,q_85/bf3085_3e0aa56a5a654f80b6b63b6cb4978b09~mv2.png';

const jobs = [
  {
    title: 'Adjoint(e)-administratif',
    location: 'St-Marc-des-Carrières',
    type: 'Temps partiel - Jour sur semaine',
    tasks: [
      'Facturation',
      'Conciliation',
      'Gestion de commandes',
      'Gestion de Caisse',
      'Comptes à payer',
      'Tâches connexes',
    ],
    note: '*Atout connaissance du logiciel Acomba',
  },
];

export default function Career() {
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', phone: '', position: '', message: '' });
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cvFile, setCvFile] = useState(null);
  const [cvUploading, setCvUploading] = useState(false);
  const [cvUrl, setCvUrl] = useState(null);
  const cvRef = useRef(null);

  const handleCvUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCvFile(file);
    setCvUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/upload-cv`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setCvUrl(data.url);
    } catch {
      setCvUrl(null);
    }
    setCvUploading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/career`, { ...form, cv_url: cvUrl });
      setStatus('success');
      setForm({ firstName: '', lastName: '', email: '', phone: '', position: '', message: '' });
      setCvFile(null);
      setCvUrl(null);
    } catch {
      setStatus('error');
    }
    setLoading(false);
  };

  return (
    <div data-testid="career-page">
      <section className="relative h-[50vh] sm:h-[60vh] flex items-center justify-center overflow-hidden" data-testid="career-hero">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Carrière" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 to-black/70" />
        </div>
        <div className="relative z-10 text-center px-4">
          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
            className="text-4xl sm:text-5xl lg:text-6xl text-white font-light" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Tu es à la recherche d'un emploi ?
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.3 }}
            className="text-xl sm:text-2xl text-white/90 mt-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            On a une place pour toi !
          </motion.p>
        </div>
      </section>

      <section className="py-20 sm:py-28 px-4" data-testid="job-listings">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <ScrollReveal>
            <img src={POSTER_IMG} alt="Offre d'emploi" className="w-full max-w-md mx-auto rounded-2xl shadow-xl" />
          </ScrollReveal>
          <StaggerContainer className="space-y-6">
            {jobs.map((job, i) => (
              <motion.div key={i} variants={staggerItem} data-testid={`job-card-${i}`}
                className="p-6 rounded-2xl transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
                style={{ backgroundColor: 'var(--color-card)', border: '1px solid var(--color-border)' }}>
                <div className="flex items-center gap-2 mb-3">
                  <MapPin size={14} style={{ color: 'var(--color-secondary)' }} />
                  <span className="text-xs uppercase tracking-widest font-semibold" style={{ color: 'var(--color-secondary)' }}>{job.location}</span>
                </div>
                <h3 className="text-xl font-semibold mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>{job.title}</h3>
                <p className="text-sm mb-4" style={{ color: 'var(--color-muted-fg)' }}>{job.type}</p>
                <ul className="space-y-1.5 mb-4">
                  {job.tasks.map((task, j) => (
                    <li key={j} className="text-sm flex items-start gap-2">
                      <span className="w-1 h-1 rounded-full mt-2 flex-shrink-0" style={{ backgroundColor: 'var(--color-primary)' }} />
                      {task}
                    </li>
                  ))}
                </ul>
                {job.note && <p className="text-xs italic" style={{ color: 'var(--color-muted-fg)' }}>{job.note}</p>}
              </motion.div>
            ))}
          </StaggerContainer>
        </div>
      </section>

      <section className="py-16 sm:py-24 px-4" style={{ backgroundColor: 'var(--color-muted)' }} data-testid="career-form-section">
        <div className="max-w-3xl mx-auto">
          <ScrollReveal>
            <h2 className="text-3xl sm:text-4xl font-light mb-2 text-center" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Travaillons ensemble
            </h2>
            <p className="text-center mb-8" style={{ color: 'var(--color-muted-fg)' }}>Contactez-nous dès maintenant !</p>
            <div className="flex justify-center mb-8">
              <a href="https://www.facebook.com/conceptgourmettraiteur" target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-base hover:text-[var(--color-primary)] transition-colors" data-testid="career-facebook">
                <Facebook size={20} /> Facebook
              </a>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={0.15}>
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="career-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-medium mb-2">Prénom</label>
                  <input type="text" required value={form.firstName} onChange={e => setForm({ ...form, firstName: e.target.value })}
                    data-testid="career-firstname-input" className="w-full px-4 py-3 rounded-xl text-base outline-none transition-all"
                    style={{ backgroundColor: 'var(--color-card)', border: '1px solid var(--color-border)', color: 'var(--color-fg)' }} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Nom de famille</label>
                  <input type="text" required value={form.lastName} onChange={e => setForm({ ...form, lastName: e.target.value })}
                    data-testid="career-lastname-input" className="w-full px-4 py-3 rounded-xl text-base outline-none transition-all"
                    style={{ backgroundColor: 'var(--color-card)', border: '1px solid var(--color-border)', color: 'var(--color-fg)' }} />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">E-mail</label>
                <input type="email" required value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
                  data-testid="career-email-input" className="w-full px-4 py-3 rounded-xl text-base outline-none transition-all"
                  style={{ backgroundColor: 'var(--color-card)', border: '1px solid var(--color-border)', color: 'var(--color-fg)' }} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Téléphone</label>
                <input type="tel" value={form.phone} onChange={e => {
                      const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
                      let fmt = digits;
                      if (digits.length > 3 && digits.length <= 6) fmt = `${digits.slice(0,3)} ${digits.slice(3)}`;
                      else if (digits.length > 6) fmt = `${digits.slice(0,3)} ${digits.slice(3,6)}-${digits.slice(6)}`;
                      setForm({ ...form, phone: fmt });
                    }}
                  placeholder="418 555-1234"
                  data-testid="career-phone-input" className="w-full px-4 py-3 rounded-xl text-base outline-none transition-all"
                  style={{ backgroundColor: 'var(--color-card)', border: '1px solid var(--color-border)', color: 'var(--color-fg)' }} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Pour quel poste ?</label>
                <input type="text" value={form.position} onChange={e => setForm({ ...form, position: e.target.value })}
                  data-testid="career-position-input" className="w-full px-4 py-3 rounded-xl text-base outline-none transition-all"
                  style={{ backgroundColor: 'var(--color-card)', border: '1px solid var(--color-border)', color: 'var(--color-fg)' }} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Message</label>
                <textarea rows={4} value={form.message} onChange={e => setForm({ ...form, message: e.target.value })}
                  data-testid="career-message-input" className="w-full px-4 py-3 rounded-xl text-base outline-none resize-none transition-all"
                  style={{ backgroundColor: 'var(--color-card)', border: '1px solid var(--color-border)', color: 'var(--color-fg)' }} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Joindre votre CV</label>
                <input type="file" ref={cvRef} accept=".pdf,.doc,.docx" className="hidden" onChange={handleCvUpload} />
                {!cvFile ? (
                  <button type="button" onClick={() => cvRef.current?.click()}
                    className="w-full flex items-center justify-center gap-2 px-4 py-4 rounded-xl text-sm font-medium transition-all hover:scale-[1.01] cursor-pointer"
                    style={{ backgroundColor: 'var(--color-card)', border: '2px dashed var(--color-border)', color: 'var(--color-muted-fg)' }}>
                    <Paperclip size={16} /> Cliquez pour joindre un fichier (PDF, DOC)
                  </button>
                ) : (
                  <div className="flex items-center gap-3 px-4 py-3 rounded-xl"
                    style={{ backgroundColor: 'var(--color-card)', border: '1px solid var(--color-border)' }}>
                    <Paperclip size={16} style={{ color: 'var(--color-primary)' }} />
                    <span className="flex-1 text-sm truncate">{cvFile.name}</span>
                    {cvUploading && <div className="w-4 h-4 border-2 border-[var(--color-primary)]/30 border-t-[var(--color-primary)] rounded-full animate-spin" />}
                    {cvUrl && <span className="text-xs font-medium px-2 py-0.5 rounded-full" style={{ backgroundColor: 'rgba(109,174,101,0.15)', color: '#6DAE65' }}>Pret</span>}
                    <button type="button" onClick={() => { setCvFile(null); setCvUrl(null); }}><X size={14} style={{ color: 'var(--color-muted-fg)' }} /></button>
                  </div>
                )}
              </div>
              <button type="submit" disabled={loading} data-testid="career-submit-btn"
                className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full text-white font-medium transition-all duration-300 hover:scale-105 hover:shadow-xl disabled:opacity-50"
                style={{ backgroundColor: 'var(--color-primary)' }}>
                {loading ? 'Envoi...' : 'Envoyer'} <Send size={16} />
              </button>
              {status === 'success' && <p data-testid="career-success" className="text-base font-medium" style={{ color: 'var(--color-secondary)' }}>Candidature envoyée avec succès !</p>}
              {status === 'error' && <p data-testid="career-error" className="text-base font-medium" style={{ color: 'var(--color-accent)' }}>Une erreur est survenue. Veuillez réessayer.</p>}
            </form>
          </ScrollReveal>

          <ScrollReveal delay={0.25}>
            <div className="mt-8 text-center p-6 rounded-2xl" style={{ backgroundColor: 'var(--color-card)', border: '1px solid var(--color-border)' }}>
              <p className="text-base font-semibold">Faites parvenir votre CV à cette adresse :</p>
              <a href="mailto:info@conceptgourmet.ca" className="text-lg font-medium hover:underline" style={{ color: 'var(--color-primary)' }}>info@conceptgourmet.ca</a>
              <p className="text-sm mt-2" style={{ color: 'var(--color-muted-fg)' }}>Merci pour votre envoi !</p>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
}
